import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, X, Shield, BookOpen, Users, Target } from 'lucide-react';

const DisclaimerBanner = ({ onDismiss }) => {
  return (
    <motion.div 
      className="disclaimer-banner"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="disclaimer-banner"
    >
      <div className="banner-content">
        <AlertTriangle size={18} />
        <p>
          <strong>Educational Content Only:</strong> BlackGirlTrading-Academy is a learning community, not financial advice. 
          We share educational resources and demo trading experiences. 
          <Link to="/disclaimer">Read full disclaimer</Link>
        </p>
      </div>
      <button onClick={onDismiss} className="dismiss-btn" data-testid="dismiss-disclaimer">
        <X size={18} />
      </button>
      
      <style>{`
        .disclaimer-banner {
          background: rgba(255, 204, 0, 0.1);
          border-bottom: 1px solid rgba(255, 204, 0, 0.3);
          padding: 12px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }
        
        .banner-content {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }
        
        .banner-content svg {
          color: var(--warning);
          flex-shrink: 0;
        }
        
        .banner-content p {
          font-size: 13px;
          color: var(--text-secondary);
          margin: 0;
        }
        
        .banner-content strong {
          color: var(--warning);
        }
        
        .banner-content a {
          color: var(--primary);
          text-decoration: underline;
          margin-left: 4px;
        }
        
        .dismiss-btn {
          background: none;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          padding: 4px;
        }
        
        .dismiss-btn:hover {
          color: var(--text-primary);
        }
      `}</style>
    </motion.div>
  );
};

const DisclaimerPage = () => {
  return (
    <div className="disclaimer-page" data-testid="disclaimer-page">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="disclaimer-header">
            <Shield size={48} />
            <h1>Important Disclaimer</h1>
            <p>Please read this carefully before using BlackGirlTrading-Academy</p>
          </div>
          
          {/* Main Disclaimer Box */}
          <div className="disclaimer-box critical">
            <h2>⚠️ Not Financial Advice</h2>
            <p>
              <strong>BlackGirlTrading-Academy is an educational platform and online learning community.</strong> 
              The content provided here is for <strong>educational and informational purposes only</strong> 
              and should not be construed as financial advice, investment recommendations, or trading signals.
            </p>
          </div>
          
          {/* What We Are */}
          <div className="what-we-are">
            <h2>What BlackGirlTrading-Academy Is</h2>
            <div className="features-grid">
              <div className="feature-item">
                <BookOpen size={24} />
                <h4>Educational Community</h4>
                <p>A space where members can follow along as we explore trading tools, platforms, strategies, and risk management principles together.</p>
              </div>
              <div className="feature-item">
                <Users size={24} />
                <h4>Shared Learning Journey</h4>
                <p>We focus on transparency, learning processes, demo practice, and shared growth rather than financial advice or trade signals.</p>
              </div>
              <div className="feature-item">
                <Target size={24} />
                <h4>Demo & Practice Focused</h4>
                <p>All trading simulations use virtual money. We emphasise practice, discipline, and the psychology required to develop trading skills.</p>
              </div>
            </div>
          </div>
          
          {/* What We Are NOT */}
          <div className="disclaimer-box warning">
            <h2>What We Are NOT</h2>
            <ul className="not-list">
              <li>
                <span className="cross">❌</span>
                <div>
                  <strong>NOT Professional Trading Education</strong>
                  <p>We are not a licensed trading academy or professional training institution.</p>
                </div>
              </li>
              <li>
                <span className="cross">❌</span>
                <div>
                  <strong>NOT Financial Advisors</strong>
                  <p>We do not provide personalised financial advice, investment recommendations, or portfolio management services.</p>
                </div>
              </li>
              <li>
                <span className="cross">❌</span>
                <div>
                  <strong>NOT Signal Providers</strong>
                  <p>We do not provide trade signals, "hot tips", or recommendations on what to buy or sell.</p>
                </div>
              </li>
              <li>
                <span className="cross">❌</span>
                <div>
                  <strong>NOT Regulated by the FCA</strong>
                  <p>BlackGirlTrading-Academy is not authorised or regulated by the Financial Conduct Authority (FCA) or any other financial regulatory body.</p>
                </div>
              </li>
            </ul>
          </div>
          
          {/* What We DO Provide */}
          <div className="disclaimer-box info">
            <h2>What We DO Provide</h2>
            <ul className="do-list">
              <li>
                <span className="check">✅</span>
                <div>
                  <strong>Beginner-Friendly Educational Resources</strong>
                  <p>Breakdowns of trading concepts, terminology, and market basics for those wanting to learn.</p>
                </div>
              </li>
              <li>
                <span className="check">✅</span>
                <div>
                  <strong>Demo Trading Practice</strong>
                  <p>Paper trading simulators using virtual money so you can practice without financial risk.</p>
                </div>
              </li>
              <li>
                <span className="check">✅</span>
                <div>
                  <strong>Insights into Trading Psychology</strong>
                  <p>Content about the mindset, discipline, and emotional management needed in trading.</p>
                </div>
              </li>
              <li>
                <span className="check">✅</span>
                <div>
                  <strong>Community Learning Experience</strong>
                  <p>A supportive environment where we learn and grow together as a community.</p>
                </div>
              </li>
            </ul>
          </div>
          
          {/* Risk Warnings */}
          <div className="disclaimer-box critical">
            <h2>⚠️ Risk Warnings</h2>
            <div className="risk-content">
              <p><strong>Trading and investing involves significant risk of loss.</strong></p>
              <ul>
                <li>You could lose some or all of your invested capital</li>
                <li>Past performance is not indicative of future results</li>
                <li>The value of investments can go down as well as up</li>
                <li>Leveraged products carry a high level of risk</li>
                <li>You should never invest money you cannot afford to lose</li>
              </ul>
              <p className="highlight">
                <strong>Before making any investment decisions, you should:</strong>
              </p>
              <ul>
                <li>Seek advice from a qualified, FCA-regulated financial advisor</li>
                <li>Conduct your own thorough research (DYOR)</li>
                <li>Consider your financial situation and risk tolerance</li>
                <li>Understand the specific risks of any financial product</li>
              </ul>
            </div>
          </div>
          
          {/* CFD Warning */}
          <div className="disclaimer-box critical">
            <h2>⚠️ CFDs and Complex Instruments Warning</h2>
            <p>
              <strong>Contracts for Difference (CFDs) are complex instruments and come with a high risk of losing 
              money rapidly due to leverage.</strong> Between 74-89% of retail investor accounts lose money when 
              trading CFDs with various providers. You should consider whether you understand how CFDs work and 
              whether you can afford to take the high risk of losing your money.
            </p>
            <p>
              Cryptocurrency investments are highly volatile and unregulated in the UK. You may not have access to 
              the Financial Ombudsman Service (FOS) or the Financial Services Compensation Scheme (FSCS) if things go wrong.
            </p>
          </div>

          {/* Age Restriction */}
          <div className="disclaimer-box warning">
            <h2>Age Restriction</h2>
            <p>
              <strong>You must be at least 18 years of age to use BlackGirlTrading-Academy.</strong> By using this platform, 
              you confirm that you are at least 18 years old. We do not permit anyone under the age of 18 to 
              access our services or content.
            </p>
          </div>

          {/* Legal */}
          <div className="disclaimer-box">
            <h2>Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, BlackGirlTrading-Academy, its owners, operators, employees, 
              agents, and contributors shall not be held liable for any direct, indirect, incidental, special, 
              consequential, or punitive damages, including but not limited to loss of profits, data, goodwill, 
              or other intangible losses arising from:
            </p>
            <ul>
              <li>Your use of or inability to use the platform</li>
              <li>Any investment or trading decisions you make</li>
              <li>Reliance on any content or information provided</li>
              <li>Unauthorised access to your account</li>
              <li>Any third-party content or conduct</li>
            </ul>
            <p>
              Nothing in this disclaimer excludes or limits our liability for death or personal injury caused by 
              our negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be 
              excluded or limited under English law.
            </p>
          </div>

          {/* Governing Law */}
          <div className="disclaimer-box">
            <h2>Governing Law and Jurisdiction</h2>
            <p>
              This disclaimer and your use of BlackGirlTrading-Academy shall be governed by and construed in accordance 
              with the laws of <strong>England and Wales</strong>. Any disputes arising from this disclaimer or 
              your use of the platform shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
            <p>
              If you are a consumer resident in the UK, you will benefit from any mandatory provisions of the 
              law of your country of residence. Nothing in this disclaimer affects your rights as a consumer 
              to rely on such mandatory provisions.
            </p>
          </div>

          {/* Acceptance */}
          <div className="disclaimer-box critical">
            <h2>Acceptance of This Disclaimer</h2>
            <p>
              <strong>By accessing or using BlackGirlTrading-Academy, you acknowledge that you have read, understood, and 
              agree to be bound by this disclaimer.</strong> If you do not agree to this disclaimer, you must 
              not use our platform.
            </p>
            <p>
              You accept full responsibility for your own investment and trading decisions. We strongly encourage 
              you to seek independent professional advice before making any financial decisions.
            </p>
          </div>
          
          {/* Contact */}
          <div className="disclaimer-footer">
            <p>If you have any questions about this disclaimer, please contact us at:</p>
            <p><strong>BlackGirlTradingAcademy@hotmail.com</strong></p>
            <div className="legal-links">
              <Link to="/terms-of-service">Terms of Service</Link>
              <span>•</span>
              <Link to="/privacy-policy">Privacy Policy</Link>
            </div>
            <p className="last-updated">Last updated: February 2026</p>
          </div>
        </motion.div>
      </div>
      
      <style>{`
        .disclaimer-page {
          padding: 40px 0 80px;
        }
        
        .disclaimer-header {
          text-align: center;
          margin-bottom: 48px;
        }
        
        .disclaimer-header svg {
          color: var(--warning);
          margin-bottom: 16px;
        }
        
        .disclaimer-header h1 {
          font-size: 2.5rem;
          margin-bottom: 12px;
        }
        
        .disclaimer-header p {
          color: var(--text-secondary);
          font-size: 1.125rem;
        }
        
        .disclaimer-box {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 32px;
          margin-bottom: 24px;
        }
        
        .disclaimer-box.critical {
          border-color: rgba(255, 59, 48, 0.3);
          background: rgba(255, 59, 48, 0.05);
        }
        
        .disclaimer-box.warning {
          border-color: rgba(255, 204, 0, 0.3);
          background: rgba(255, 204, 0, 0.05);
        }
        
        .disclaimer-box.info {
          border-color: rgba(0, 255, 148, 0.3);
          background: rgba(0, 255, 148, 0.05);
        }
        
        .disclaimer-box h2 {
          font-size: 1.5rem;
          margin-bottom: 16px;
        }
        
        .disclaimer-box p {
          color: var(--text-secondary);
          line-height: 1.7;
          margin-bottom: 12px;
        }
        
        .disclaimer-box p:last-child {
          margin-bottom: 0;
        }
        
        .disclaimer-box strong {
          color: var(--text-primary);
        }
        
        /* What We Are */
        .what-we-are {
          margin-bottom: 24px;
        }
        
        .what-we-are h2 {
          font-size: 1.5rem;
          margin-bottom: 24px;
        }
        
        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        
        .feature-item {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 24px;
          text-align: center;
        }
        
        .feature-item svg {
          color: var(--primary);
          margin-bottom: 12px;
        }
        
        .feature-item h4 {
          margin-bottom: 8px;
        }
        
        .feature-item p {
          color: var(--text-secondary);
          font-size: 14px;
          line-height: 1.6;
        }
        
        /* Lists */
        .not-list, .do-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .not-list li, .do-list li {
          display: flex;
          gap: 16px;
          padding: 16px 0;
          border-bottom: 1px solid var(--border);
        }
        
        .not-list li:last-child, .do-list li:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
        
        .cross, .check {
          font-size: 20px;
          flex-shrink: 0;
        }
        
        .not-list strong, .do-list strong {
          display: block;
          margin-bottom: 4px;
        }
        
        .not-list p, .do-list p {
          font-size: 14px;
          margin: 0;
        }
        
        /* Risk Content */
        .risk-content ul {
          margin: 16px 0;
          padding-left: 20px;
        }
        
        .risk-content li {
          color: var(--text-secondary);
          margin-bottom: 8px;
        }
        
        .risk-content .highlight {
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid var(--border);
        }
        
        /* Footer */
        .disclaimer-footer {
          text-align: center;
          margin-top: 48px;
          padding-top: 24px;
          border-top: 1px solid var(--border);
        }
        
        .disclaimer-footer p {
          color: var(--text-muted);
          font-size: 14px;
        }

        .disclaimer-footer strong {
          color: var(--primary);
        }

        .legal-links {
          display: flex;
          justify-content: center;
          gap: 16px;
          margin: 16px 0;
        }

        .legal-links a {
          color: var(--primary);
          text-decoration: none;
        }

        .legal-links a:hover {
          text-decoration: underline;
        }

        .legal-links span {
          color: var(--text-muted);
        }
        
        .last-updated {
          margin-top: 8px;
          font-size: 12px !important;
        }

        .disclaimer-box ul {
          margin: 16px 0;
          padding-left: 24px;
        }

        .disclaimer-box li {
          color: var(--text-secondary);
          margin-bottom: 8px;
          line-height: 1.6;
        }
        
        @media (max-width: 768px) {
          .features-grid {
            grid-template-columns: 1fr;
          }
          
          .disclaimer-box {
            padding: 24px;
          }
        }
      `}</style>
    </div>
  );
};

export { DisclaimerBanner, DisclaimerPage };
export default DisclaimerPage;
